import { z } from 'zod';
/**
 * In-app notification contracts (header bell + dropdown). One row per recipient; read state is
 * per-user. A notification with no `ctaLabel`/`ctaHref` is notification-only (no button).
 */
export const notificationSeveritySchema = z.enum(['info', 'success', 'warning', 'error']);
/**
 * Job lifecycle: 'running' (a background job in progress - shown immediately + rendered with a live
 * indicator), 'success' | 'failed' (terminal), or 'info' (a static, non-job notification).
 */
export const notificationStatusSchema = z.enum(['running', 'success', 'failed', 'info']);
/** The notification as the web bell/panel consumes it. */
export const notificationDtoSchema = z.object({
    uuid: z.string(),
    kind: z.string(),
    status: notificationStatusSchema,
    severity: notificationSeveritySchema,
    title: z.string(),
    body: z.string(),
    ctaLabel: z.string().nullable().optional(),
    ctaHref: z.string().nullable().optional(),
    metadata: z.record(z.unknown()).nullable().optional(),
    readAt: z.string().nullable().optional(),
    createdAt: z.string(),
});
/**
 * A 'running' notification this old (with no terminal update) is treated as INTERRUPTED: its producing
 * job almost certainly died without finalizing it (e.g. a server restart / dev hot-reload kills the
 * detached background job, so the row is stranded at status='running' forever). `createdAt` is the
 * start time and is never bumped while running, so its age is the run duration. 10 min comfortably
 * clears legitimate multi-minute jobs (corpus auto-tune, ingestion) while flagging a truly stuck one.
 */
export const NOTIFICATION_STALE_RUNNING_MS = 10 * 60 * 1000;
/** True when a notification is stuck 'running' past {@link NOTIFICATION_STALE_RUNNING_MS} (interrupted). */
export function isNotificationStaleRunning(n, nowMs = Date.now()) {
    if (n.status !== 'running')
        return false;
    const started = new Date(n.createdAt).getTime();
    return Number.isFinite(started) && nowMs - started > NOTIFICATION_STALE_RUNNING_MS;
}
export const listNotificationsResponseSchema = z.object({
    notifications: z.array(notificationDtoSchema),
    unreadCount: z.number(),
});
//# sourceMappingURL=notification.js.map